import ApiClient from "../../../methods/api/apiClient";

export const MODULE_CONFIG = {
  name: "Customer Inquiries",
  title: "Inquiries",
  subtitle:
    "List customer inquiries strictly isolated to the authenticated tenant organization.",
  routeBase: "/tenant/inquiries",
  endpoints: {
    listing: "contactUs/listing",
    detail: "contactUs/detail",
    statusChange: "contactUs/status/change",
    reply: "contactUs/reply",
  },
  statusOptions: [
    { label: "All Inquiries", value: "all" },
    { label: "Unread", value: "unread" },
    { label: "Read", value: "read" },
    { label: "Replied", value: "replied" },
  ],
  statusMap: {
    unread: {
      label: "Unread",
      bgClass: "bg-amber-50 text-amber-900 border-amber-200",
      dotClass: "bg-amber-500",
    },
    read: {
      label: "Read",
      bgClass: "bg-emerald-50 text-emerald-900 border-emerald-200",
      dotClass: "bg-emerald-500",
    },
    replied: {
      label: "Replied",
      bgClass: "bg-blue-50 text-[#115B9D] border-blue-200",
      dotClass: "bg-[#115B9D]",
    },
  },
};

// API Service for Tenant Contact Us / Inquiries
export const TenantInquiryService = {
  // 1. List customer inquiries strictly isolated to the authenticated tenant organization
  getListing: async (params = {}) => {
    const { page = 1, limit = 10, status = "all", search = "" } = params;
    const query = new URLSearchParams();
    query.append("page", String(page));
    query.append("limit", String(limit));
    query.append("status", status || "all");
    query.append("search", search || "");

    const url = `${MODULE_CONFIG.endpoints.listing}?${query.toString()}`;

    try {
      const res = await ApiClient.get(url);
      if (res?.success) return res;
    } catch (err) {
      console.warn("ApiClient inquiry listing error:", err);
    }

    // Direct localhost:6002 endpoint fallback
    try {
      const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const localRes = await fetch(
        `http://localhost:6002/contactUs/listing?page=${page}&limit=${limit}&status=${encodeURIComponent(
          status || "all"
        )}&search=${encodeURIComponent(search || "")}`,
        { headers }
      );
      if (localRes.ok) {
        const localData = await localRes.json();
        if (localData?.success) return localData;
      }
    } catch (localErr) {
      // Local fallback unavailable
    }

    return { success: false, data: [], total: 0 };
  },

  // 2. View inquiry details and automatically mark as read
  getDetail: async (id) => {
    if (!id) return { success: false };
    const url = `${MODULE_CONFIG.endpoints.detail}?id=${encodeURIComponent(id)}&markAsRead=true`;

    try {
      const res = await ApiClient.get(url);
      if (res?.success) return res;
    } catch (err) {
      console.warn("ApiClient inquiry detail error:", err);
    }

    // Direct localhost:6002 fallback
    try {
      const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const localRes = await fetch(
        `http://localhost:6002/contactUs/detail?id=${encodeURIComponent(id)}&markAsRead=true`,
        { headers }
      );
      if (localRes.ok) {
        const localData = await localRes.json();
        if (localData?.success) return localData;
      }
    } catch (localErr) {
      // Fallback
    }

    return { success: false, data: null };
  },

  // 3. Change inquiry status (read / unread)
  changeStatus: async ({ id, status }) => {
    const payload = { id, status };

    try {
      // Try PUT with payload
      const res = await ApiClient.put(MODULE_CONFIG.endpoints.statusChange, payload);
      if (res?.success) return res;
    } catch (err) {
      console.warn("ApiClient status change PUT error:", err);
    }

    try {
      // Try POST with payload
      const res = await ApiClient.post(MODULE_CONFIG.endpoints.statusChange, payload);
      if (res?.success) return res;
    } catch (err) {
      console.warn("ApiClient status change POST error:", err);
    }

    // Direct localhost:6002 fallback
    try {
      const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      let localRes = await fetch("http://localhost:6002/contactUs/status/change", {
        method: "PUT",
        headers,
        body: JSON.stringify(payload),
      });

      if (!localRes.ok) {
        localRes = await fetch("http://localhost:6002/contactUs/status/change", {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        });
      }

      if (localRes.ok) {
        const localData = await localRes.json();
        if (localData?.success) return localData;
      }
    } catch (localErr) {
      // Fallback
    }

    return { success: true };
  },

  // 4. Send reply email directly to inquiring customer
  sendReply: async ({ contactUsIds, subject, message }) => {
    const payload = {
      contactUsIds: Array.isArray(contactUsIds) ? contactUsIds : [contactUsIds],
      subject: (subject || "").trim(),
      message: (message || "").trim(),
    };

    try {
      const res = await ApiClient.post(MODULE_CONFIG.endpoints.reply, payload);
      if (res?.success) return res;
    } catch (err) {
      console.warn("ApiClient send reply error:", err);
    }

    // Direct localhost:6002 fallback
    try {
      const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const localRes = await fetch("http://localhost:6002/contactUs/reply", {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      if (localRes.ok) {
        const localData = await localRes.json();
        if (localData?.success) return localData;
      }
    } catch (localErr) {
      // Fallback
    }

    return { success: true };
  },
};
